import React, { useMemo, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Space,
  Spin,
} from "antd";
import moment from "moment";
import type { Moment } from "moment";
import type { FormProps } from "antd/es/form/Form";
import type { EmptyFunction, ISearchFilterField } from "../../../../lib/types";
import type { IOfferingGroupsSearch } from "../../types/IOfferingGroupsSearch";
import { BulkFiltersToggler } from "../../../../components/BulkFiltersToggler/BulkFiltersToggler";
import { useAppSelector } from "../../../..";
import { checkIsLoading, displayFormat, getSelectOptions } from "../../../../lib/utils";
import "./OfferingGroupsSearch.less";
import { PolicyTypeId } from "../../types/IPolicy";

interface ComponentProps {
  isSearchOpen: boolean;
  policyTypeId?: number;
  isParamPlace?: "first" | "second";
  toggleSearchOpen: EmptyFunction;
  onSearch: () => void;
  onResetSearch: EmptyFunction;
  searchForm: FormInstance<IOfferingGroupsSearch<Moment>>;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const OfferingGroupsSearch = (props: ComponentProps) => {
  const {
    isSearchOpen,
    toggleSearchOpen,
    onSearch,
    onResetSearch,
    searchForm,
    policyTypeId,
    isParamPlace,
  } = props;
  const {
    offeringGroupsLoading,
    offeringGroupsTypesDic,
    offeringGroupsTypesDicLoading,
  } = useAppSelector((state) => state.offeringGroups);

  const onFinish: FormProps["onFinish"] = () => {
    onSearch();
  };

  const filterOfferingGroupsTypesDicForPolicyType = policyTypeId
    ? offeringGroupsTypesDic?.filter((groupTypeEl) => {
        switch (policyTypeId) {
          case PolicyTypeId.INTERACTION:
            return ["FOREACH"].some((el) => el === groupTypeEl.abbrev);
          case PolicyTypeId.REQUIRE:
            if (isParamPlace === "first") {
              return ["OR", "AND", "XOR"].some((el) => el === groupTypeEl.abbrev);
            }
            return ["OR"].some((el) => el === groupTypeEl.abbrev);
          case PolicyTypeId.VALIDATE:
            return ["OR", "AND", "XOR"].some((el) => el === groupTypeEl.abbrev);
          default:
            return true;
        }
      })
    : offeringGroupsTypesDic;

  const allFields: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Наименование",
        fieldElement: (
          <Form.Item label="Наименование" name="name">
            <Input allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Тип группы",
        fieldElement: (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            label="Тип группы"
            name="groupingTypeId"
            rules={[{ required: !!policyTypeId, message: "" }]}
          >
            <Select
              options={getSelectOptions({
                list: filterOfferingGroupsTypesDicForPolicyType,
              })}
              loading={checkIsLoading(offeringGroupsTypesDicLoading)}
              placeholder="Выбрать"
              allowClear
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Дата",
        fieldElement: (
          <Form.Item label="Дата" name="onDate">
            <DatePicker format={displayFormat} allowClear={false} />
          </Form.Item>
        ),
        visible: true,
      },
    ],
    [offeringGroupsTypesDic, policyTypeId, isParamPlace]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>(allFields);

  return (
    <Collapse
      expandIconPosition="right"
      className="offering-groups-search"
      activeKey={isSearchOpen ? "1" : ""}
      onChange={toggleSearchOpen}
    >
      <Collapse.Panel header="Расширенный поиск" key="1">
        <Row justify="end">
          <BulkFiltersToggler
            allFields={allFields}
            setFields={setFields}
            filterName="offering-groups-search"
            minSave={["Дата"]}
          />
        </Row>
        <Form
          form={searchForm}
          onFinish={onFinish}
          wrapperCol={{ span: 16 }}
          labelCol={{ span: 7 }}
          labelAlign="left"
          initialValues={{
            onDate: moment(),
          }}
          onReset={onResetSearch}
        >
          <Row gutter={50}>
            {fields
              .filter(({ visible }) => visible)
              .map(({ fieldElement }, idx) => (
                <Col span={8} key={idx}>
                  {fieldElement}
                </Col>
              ))}
          </Row>
          <Row justify="end">
            <Space size={20}>
              <Button htmlType="reset">Очистить</Button>
              <Spin spinning={checkIsLoading(offeringGroupsLoading)}>
                <Button type="primary" htmlType="submit">
                  Применить
                </Button>
              </Spin>
            </Space>
          </Row>
        </Form>
      </Collapse.Panel>
    </Collapse>
  );
};
